import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';

import styles from './ImageMarquee.module.css';

import type { PictureSources } from '@farmersdog/corgi-x';
import { Picture, Text, useIsomorphicLayoutEffect } from '@farmersdog/corgi-x';
import { useMarqueeAnimation } from './hooks/useMarqueeAnimation';
import { usePreventManualScroll } from './hooks/usePreventManualScroll';

interface MarqueeItemProps {
  index: number;
  name: string;
  state: string;
  sources: PictureSources[];
}

interface ImageMarqueeProps {
  direction: 'left' | 'right' | 'up' | 'down';
  speed?: number;
  className?: string;
  items: MarqueeItemProps[];
  ariaLabel: string;
}

const MarqueeItem = ({ name, state, sources, index }: MarqueeItemProps) => {
  return (
    <div className={styles.imageContainer} data-index={`${name}-${index}`}>
      <div className={styles.imageTagContainer}>
        <Text className={styles.imageTag} color="white" bold>
          {name}
        </Text>
        <Text className={styles.imageTag} color="white">
          {' | '}
          {state}
        </Text>
      </div>
      <Picture
        sources={sources}
        aria-hidden
        alt=""
        className={styles.image}
        key={`picture-${name}-${index}`}
        loading="lazy"
      />
    </div>
  );
};

/**
 *
 * The ImageMarquee component creates a continuous scrolling display of images with short captions,
 * commonly used to showcase customer photos.
 *
 */
export function ImageMarquee({
  direction = 'left',
  speed = 45,
  className,
  items = [],
  ariaLabel,
}: ImageMarqueeProps) {
  const isVertical = direction === 'up' || direction === 'down';
  const [isPaused, setIsPaused] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const surroundingBackup = 2;

  const pauseMarquee = useCallback(() => setIsPaused(true), []);
  const resumeMarquee = useCallback(() => setIsPaused(false), []);

  const setupScrollPosition = useCallback(() => {
    if (!containerRef.current || !contentRef.current || !scrollRef.current) {
      return false;
    }

    const scrollElement = scrollRef.current;

    if (isVertical) {
      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = contentRef.current.offsetHeight;

      // Set the scroll container height
      scrollElement.style.height = `${containerHeight}px`;

      // Calculate offset based on direction
      const offset = direction === 'down' ? 0.5 : 0;
      scrollElement.scrollTop = contentHeight * (surroundingBackup + offset);
    } else {
      const contentWidth = contentRef.current.offsetWidth;

      // Calculate offset based on direction
      const offset = direction === 'right' ? 0.5 : 0;
      scrollElement.scrollLeft = contentWidth * (surroundingBackup + offset);
    }

    return true; // Return true if successful
  }, [isVertical, direction, surroundingBackup]);

  // Initial setup
  useIsomorphicLayoutEffect(() => {
    const timeoutId = setTimeout(() => {
      setupScrollPosition();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [setupScrollPosition]);

  const handleResize = useCallback(() => {
    setIsResizing(true);

    if (!setupScrollPosition()) {
      setTimeout(() => setIsResizing(false), 150);
      return;
    }

    setTimeout(() => setIsResizing(false), 150);
  }, [setupScrollPosition]);

  // Handle dimension change
  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout>;
    let previousHeight: number | undefined;
    let previousWidth: number | undefined;

    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);

      if (!containerRef.current) {
        return;
      }

      const currentHeight = containerRef.current.clientHeight;
      const currentWidth = containerRef.current.clientWidth;

      const shouldResize = isVertical
        ? currentHeight !== previousHeight
        : currentWidth !== previousWidth;

      if (shouldResize) {
        previousHeight = currentHeight;
        previousWidth = currentWidth;

        if (scrollRef.current) {
          setIsResizing(true);
        }

        resizeTimeout = setTimeout(() => {
          handleResize();
        }, 100);
      }
    });

    if (containerRef.current) {
      previousHeight = containerRef.current.clientHeight;
      previousWidth = containerRef.current.clientWidth;

      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, [handleResize, isVertical]);

  // Handle visibility change
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          handleResize();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [handleResize]);

  useMarqueeAnimation(scrollRef, isPaused, isResizing, direction, speed);
  usePreventManualScroll(scrollRef);

  const backupContent = useMemo(
    () => Array(surroundingBackup).fill(null) as React.ReactNode[],
    [surroundingBackup]
  );

  return (
    <div
      className={classNames(styles.wrapper, className)}
      ref={containerRef}
      onMouseEnter={pauseMarquee}
      onMouseLeave={resumeMarquee}
      onTouchStart={pauseMarquee}
      onTouchEnd={resumeMarquee}
      onFocus={pauseMarquee}
      onBlur={resumeMarquee}
      role="marquee"
      aria-label={ariaLabel}
      aria-live="off"
    >
      <div
        ref={scrollRef}
        className={classNames(styles.marqueeScroll, {
          [styles.vertical]: isVertical,
          [styles.resizing]: isResizing,
        })}
      >
        {/* Backup content before */}
        {backupContent.map((_, index) => (
          <div
            key={`before-${index}`}
            className={classNames(styles.container, {
              [styles.vertical]: isVertical,
            })}
          >
            {items.map(({ name, state, sources }, itemIndex) => (
              <MarqueeItem
                key={`before-item-${itemIndex}`}
                index={itemIndex}
                name={name}
                state={state}
                sources={sources}
              />
            ))}
          </div>
        ))}

        {/* Main content */}
        <div
          ref={contentRef}
          className={classNames(styles.container, {
            [styles.vertical]: isVertical,
          })}
        >
          {items.map(({ name, state, sources }, index) => (
            <MarqueeItem
              key={`main-item-${index}`}
              index={index}
              name={name}
              state={state}
              sources={sources}
            />
          ))}
        </div>

        {/* Backup content after */}
        {backupContent.map((_, index) => (
          <div
            key={`after-${index}`}
            className={classNames(styles.container, {
              [styles.vertical]: isVertical,
            })}
          >
            {items.map(({ name, state, sources }, itemIndex) => (
              <MarqueeItem
                key={`after-item-${itemIndex}`}
                index={itemIndex}
                name={name}
                state={state}
                sources={sources}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
